import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import DatePicker from '../components/common/DatePicker';
import { ValidationService } from '../services/validationService';
import { SolicitacoesService } from '../services/solicitacoesService';
import { SolicitacaoAgendamento, StatusSolicitacao } from '../types';
import { AuditService } from '../services/auditService';
import { TipoAcaoAudit } from '../types';

const Solicitacoes: React.FC = () => {
  const { user } = useAuth();
  const { filialAtual } = useApp();
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoAgendamento[]>([]);
  const [filteredSolicitacoes, setFilteredSolicitacoes] = useState<SolicitacaoAgendamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSolicitacao, setEditingSolicitacao] = useState<SolicitacaoAgendamento | null>(null);
  const [showDocumentosModal, setShowDocumentosModal] = useState(false);
  const [solicitacaoDocumentos, setSolicitacaoDocumentos] = useState<SolicitacaoAgendamento | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    fornecedor: '',
    data: ''
  });

  useEffect(() => {
    carregarSolicitacoes();
  }, [filialAtual]);

  useEffect(() => {
    aplicarFiltros();
  }, [solicitacoes, filters]);

  const carregarSolicitacoes = async () => {
    try {
      setLoading(true);
      const dados = await SolicitacoesService.buscarTodas({ filial: filialAtual });
      setSolicitacoes(dados);
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    let filtrados = [...solicitacoes];
    
    if (filters.status) {
      filtrados = filtrados.filter(s => s.status === filters.status);
    }

    if (filters.fornecedor) {
      filtrados = filtrados.filter(s => 
        s.fornecedor.toLowerCase().includes(filters.fornecedor.toLowerCase())
      );
    }

    if (filters.data) {
      filtrados = filtrados.filter(s => s.data_desejada === filters.data);
    }

    setFilteredSolicitacoes(filtrados);
  };

  // Verificar permissões (baseado no sistema antigo)
  const temPermissao = (permissao: string) => {
    if (!user) return false;
    
    const permissoes = {
      admin: ['adicionarCargas', 'editarCargas', 'excluirCargas', 'aprovarAgendamentos', 'gerarRelatorios', 'solicitarAgendamento'],
      gerente: ['adicionarCargas', 'editarCargas', 'aprovarAgendamentos', 'gerarRelatorios', 'solicitarAgendamento'],
      operador: ['visualizarCargas', 'adicionarCargas', 'solicitarAgendamento'],
      cliente: ['solicitarAgendamento']
    };
    
    return permissoes[user.perfil as keyof typeof permissoes]?.includes(permissao) || false;
  };

  const handleNovaSolicitacao = () => {
    if (!temPermissao('solicitarAgendamento')) {
      alert('Você não tem permissão para solicitar agendamentos!');
      return;
    }
    setEditingSolicitacao(null);
    setShowModal(true);
  };

  const handleVerDocumentos = (solicitacao: SolicitacaoAgendamento) => {
    setSolicitacaoDocumentos(solicitacao);
    setShowDocumentosModal(true);
  };

  const handleAprovar = async (id: string) => {
    if (!temPermissao('aprovarAgendamentos')) {
      alert('Você não tem permissão para aprovar solicitações!');
      return;
    }

    try {
      const solicitacao = solicitacoes.find(s => s.id === id);
      if (!solicitacao) return;

      await SolicitacoesService.aprovarSolicitacao(id);

      // Registrar no audit trail
      await AuditService.registrarAcao({
        usuarioId: user?.id || '',
        usuarioNome: user?.nome || '',
        acao: TipoAcaoAudit.ATUALIZAR,
        tabela: 'solicitacoes_agendamento',
        registroId: id,
        valoresAntigos: { status: solicitacao.status },
        valoresNovos: { status: StatusSolicitacao.AGUARDANDO_PEDIDO }
      });

      await carregarSolicitacoes();
      alert('Solicitação aprovada com sucesso!');
    } catch (error) {
      console.error('Erro ao aprovar solicitação:', error);
      alert('Erro ao aprovar solicitação');
    }
  };

  const handleAceitarReagendamento = async (id: string) => {
    if (!window.confirm('Deseja aceitar o reagendamento? A solicitação será aprovada e convertida em carga.')) return;
    try {
      const { error } = await SolicitacoesService.aceitarReagendamento(id);
      if (error) {
        alert(error.message || 'Erro ao aceitar reagendamento');
        return;
      }
      await carregarSolicitacoes();
      alert('Reagendamento aceito! Carga criada com sucesso.');
    } catch (error) {
      console.error('Erro ao aceitar reagendamento:', error);
      alert('Erro ao aceitar reagendamento');
    }
  };

  const handleRejeitarReagendamento = async (id: string) => {
    const motivo = window.prompt('Motivo da rejeição do reagendamento:');
    if (!motivo) return;
    try {
      const { error } = await SolicitacoesService.rejeitarReagendamento(id, motivo);
      if (error) {
        alert(error.message || 'Erro ao rejeitar reagendamento');
        return;
      }
      await carregarSolicitacoes();
      alert('Reagendamento rejeitado. A solicitação voltou para status Pendente.');
    } catch (error) {
      console.error('Erro ao rejeitar reagendamento:', error);
      alert('Erro ao rejeitar reagendamento');
    }
  };

  const handleRejeitar = async (id: string) => {
    if (!temPermissao('aprovarAgendamentos')) {
      alert('Você não tem permissão para rejeitar solicitações!');
      return;
    }

    const motivo = window.prompt('Motivo da rejeição:');
    if (motivo) {
      try {
        const solicitacao = solicitacoes.find(s => s.id === id);
        if (!solicitacao) return;

        await SolicitacoesService.reprovarSolicitacao(id, motivo);

        // Registrar no audit trail
        await AuditService.registrarAcao({
          usuarioId: user?.id || '',
          usuarioNome: user?.nome || '',
          acao: TipoAcaoAudit.ATUALIZAR,
          tabela: 'solicitacoes_agendamento',
          registroId: id,
          valoresAntigos: { status: solicitacao.status },
          valoresNovos: { status: StatusSolicitacao.REPROVADO },
          motivo: motivo
        });

        await carregarSolicitacoes();
        alert('Solicitação rejeitada!');
      } catch (error) {
        console.error('Erro ao rejeitar solicitação:', error);
        alert('Erro ao rejeitar solicitação');
      }
    }
  };

  const handleSaveSolicitacao = async (data: any) => {
    if (editingSolicitacao) {
      alert('Edição não implementada ainda');
      return;
    } else {
      // Nova solicitação - validar dia fechado primeiro
      const validacaoDia = await ValidationService.validarDiaFechado(
        data.dataDesejada,
        filialAtual,
        data.tipoProduto
      );
      
      if (!validacaoDia.isValid) {
        alert(validacaoDia.errors.join('\n'));
        return;
      }
      
      try {
        const resultado = await SolicitacoesService.criarSolicitacao({
          solicitante_id: user?.id || '',
          solicitante_nome: user?.nome || 'Usuário',
          fornecedor: data.fornecedor,
          fornecedor_mercadoria: data.fornecedorMercadoria,
          data_desejada: data.dataDesejada,
          peso: parseFloat(data.peso),
          volume: data.volume,
          tipo_produto: data.tipoProduto,
          tipo_carga: data.tipoCarga,
          conferente: data.conferente,
          status: StatusSolicitacao.PENDENTE,
          observacoes: data.observacoes,
          data_solicitacao: new Date().toISOString(),
          data_atualizacao: new Date().toISOString(),
          filial: filialAtual
        });

        if (!resultado.data) {
          alert('Erro ao criar solicitação');
          return;
        }

        // Registrar no audit trail
        await AuditService.registrarAcao({
          usuarioId: user?.id || '',
          usuarioNome: user?.nome || '',
          acao: TipoAcaoAudit.CRIAR,
          tabela: 'solicitacoes_agendamento',
          registroId: resultado.data.id,
          valoresAntigos: {},
          valoresNovos: {
            fornecedor: data.fornecedor,
            data_desejada: data.dataDesejada,
            status: StatusSolicitacao.PENDENTE
          }
        });

        await carregarSolicitacoes();
        setShowModal(false);
        alert('Solicitação criada com sucesso!');
      } catch (error) {
        console.error('Erro ao criar solicitação:', error);
        alert('Erro ao criar solicitação');
      }
    }
  };

  const getStatusColor = (status: StatusSolicitacao) => {
    switch (status) {
      case StatusSolicitacao.APROVADO: return 'bg-green-100 text-green-800';
      case StatusSolicitacao.PENDENTE: return 'bg-blue-100 text-blue-800';
      case StatusSolicitacao.REPROVADO: return 'bg-red-100 text-red-800';
      case StatusSolicitacao.AGUARDANDO_PEDIDO: return 'bg-blue-100 text-blue-800';
      case StatusSolicitacao.PEDIDO_DIGITADO: return 'bg-purple-100 text-purple-800';
      case StatusSolicitacao.REAGENDADO: return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatarData = (dataString: string) => {
    if (!dataString) return '';
    // Converter string para Date usando fuso horário local para evitar problemas de UTC
    if (dataString.includes('T')) {
      const [ano, mes, dia] = dataString.split('T')[0].split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR');
    } else {
      const [ano, mes, dia] = dataString.split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR');
    }
  };

  const formatarTamanho = (bytes: number) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Solicitações de Agendamento</h1>
          {temPermissao('solicitarAgendamento') && (
            <Button onClick={handleNovaSolicitacao}>
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Solicitação
            </Button>
          )}
        </div>

        {/* Filtros */}
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Todos</option>
                <option value={StatusSolicitacao.PENDENTE}>Solicitado</option>
                <option value={StatusSolicitacao.AGUARDANDO_PEDIDO}>Aguardando Pedido</option>
                <option value={StatusSolicitacao.PEDIDO_DIGITADO}>Pedido Digitado</option>
                <option value={StatusSolicitacao.APROVADO}>Aprovado</option>
                <option value={StatusSolicitacao.REPROVADO}>Reprovado</option>
                <option value={StatusSolicitacao.REAGENDADO}>Reagendado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
              <Input
                placeholder="Buscar fornecedor..."
                value={filters.fornecedor}
                onChange={(value) => setFilters(prev => ({ ...prev, fornecedor: value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Desejada</label>
              <Input
                type="date"
                value={filters.data}
                onChange={(value) => setFilters(prev => ({ ...prev, data: value }))}
              />
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => setFilters({ status: '', fornecedor: '', data: '' })}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fornecedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo Produto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Peso (kg)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Desejada
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSolicitacoes.map((solicitacao) => (
                  <tr key={solicitacao.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {solicitacao.id.substring(0, 8)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{solicitacao.fornecedor}</div>
                        <div className="text-gray-500">{solicitacao.conferente}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        solicitacao.tipo_produto === 'CONGELADOS' ? 'bg-cyan-100 text-cyan-800' : solicitacao.tipo_produto === 'REFRIGERADOS' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {solicitacao.tipo_produto}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {solicitacao.peso.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatarData(solicitacao.data_desejada)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(solicitacao.status)}`}>
                        {solicitacao.status}
                      </span>
                      {solicitacao.status === StatusSolicitacao.REAGENDADO && solicitacao.reagendamento_proposto && (
                        <div className="mt-1 text-xs text-orange-600">
                          Nova data: {formatarData(solicitacao.reagendamento_proposto.novaData)}
                          <br />{solicitacao.reagendamento_proposto.motivo}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {temPermissao('aprovarAgendamentos') && solicitacao.status === StatusSolicitacao.PENDENTE && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAprovar(solicitacao.id)}
                            >
                              Aprovar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRejeitar(solicitacao.id)}
                            >
                              Rejeitar
                            </Button>
                          </>
                        )}
                        {solicitacao.status === StatusSolicitacao.REAGENDADO && solicitacao.reagendamento_proposto && (
                          <>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleAceitarReagendamento(solicitacao.id)}
                            >
                              Aceitar Reagendamento
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRejeitarReagendamento(solicitacao.id)}
                            >
                              Rejeitar Reagendamento
                            </Button>
                          </>
                        )}
                        {solicitacao.documentos && solicitacao.documentos.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleVerDocumentos(solicitacao)}
                          >
                            Documentos
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingSolicitacao(solicitacao);
                            setShowModal(true);
                          }}
                        >
                          Ver
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredSolicitacoes.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">Nenhuma solicitação encontrada</p>
            </div>
          )}
        </div>

        {/* Estatísticas */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Total de Solicitações</div>
            <div className="text-2xl font-bold text-gray-900">{solicitacoes.length}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Pendentes</div>
            <div className="text-2xl font-bold text-yellow-600">
              {solicitacoes.filter(s => s.status === StatusSolicitacao.PENDENTE).length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Aprovadas</div>
            <div className="text-2xl font-bold text-green-600">
              {solicitacoes.filter(s => s.status === StatusSolicitacao.APROVADO).length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Rejeitadas</div>
            <div className="text-2xl font-bold text-red-600">
              {solicitacoes.filter(s => s.status === StatusSolicitacao.REPROVADO).length}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingSolicitacao ? 'Detalhes da Solicitação' : 'Nova Solicitação'}
        size="lg"
      >
        <SolicitacaoForm
          solicitacao={editingSolicitacao}
          onSave={handleSaveSolicitacao}
          onCancel={() => setShowModal(false)}
          readOnly={!!editingSolicitacao}
        />
      </Modal>

      {/* Modal de Documentos */}
      <Modal
        isOpen={showDocumentosModal}
        onClose={() => setShowDocumentosModal(false)}
        title="Documentos da Solicitação"
        size="md"
      >
        {solicitacaoDocumentos && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <p><strong>Fornecedor:</strong> {solicitacaoDocumentos.fornecedor}</p>
              <p><strong>Data Desejada:</strong> {formatarData(solicitacaoDocumentos.data_desejada)}</p>
            </div>
            
            {solicitacaoDocumentos.documentos && solicitacaoDocumentos.documentos.length > 0 ? (
              <div className="space-y-3">
                {solicitacaoDocumentos.documentos.map((doc, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{doc.nome}</p>
                        <p className="text-sm text-gray-500">{doc.tipo}</p>
                        <p className="text-xs text-gray-400">
                          {doc.tamanho ? formatarTamanho(doc.tamanho) : 'N/A'} • 
                          {doc.uploadedBy ? ` Enviado por ${doc.uploadedBy}` : ''}
                        </p>
                      </div>
                      {doc.url && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Abrir
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Nenhum documento anexado a esta solicitação.</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

// Formulário de Solicitação
interface SolicitacaoFormProps {
  solicitacao: any;
  onSave: (data: any) => void;
  onCancel: () => void;
  readOnly?: boolean;
}

const SolicitacaoForm: React.FC<SolicitacaoFormProps> = ({ solicitacao, onSave, onCancel, readOnly = false }) => {
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    fornecedor: solicitacao?.fornecedor || '',
    peso: solicitacao?.peso?.toString() || '',
    volume: solicitacao?.volume?.toString() || '',
    tipoProduto: solicitacao?.tipo_produto || '',
    tipoCarga: solicitacao?.tipo_carga || '',
    dataDesejada: solicitacao?.data_desejada || new Date().toISOString().split('T')[0],
    conferente: solicitacao?.conferente || '',
    observacoes: solicitacao?.observacoes || ''
  });

  // Preencher fornecedor automaticamente para perfil fornecedor
  useEffect(() => {
    if (user?.perfil === 'fornecedor' && !solicitacao) {
      setFormData(prev => ({
        ...prev,
        fornecedor: user.nome || ''
      }));
    }
  }, [user, solicitacao]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};

    if (!formData.fornecedor.trim()) newErrors.fornecedor = 'Fornecedor é obrigatório';
    if (!formData.peso || parseFloat(formData.peso) <= 0) {
      newErrors.peso = 'Peso deve ser maior que 0';
    }
    if (!formData.volume || parseInt(formData.volume) <= 0) {
      newErrors.volume = 'Volume deve ser maior que 0';
    }
    if (!formData.tipoProduto) newErrors.tipoProduto = 'Tipo de produto é obrigatório';
    if (!formData.tipoCarga) newErrors.tipoCarga = 'Tipo de carga é obrigatório';
    if (!formData.dataDesejada) newErrors.dataDesejada = 'Data desejada é obrigatória';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="Fornecedor"
            value={formData.fornecedor}
            onChange={(value) => handleChange('fornecedor', value)}
            error={errors.fornecedor}
            placeholder="Nome do fornecedor"
            required
            disabled={readOnly || user?.perfil === 'fornecedor'}
          />
        </div>
        <div>
          <Input
            label="Peso Estimado (kg)"
            type="number"
            value={formData.peso}
            onChange={(value) => handleChange('peso', value)}
            error={errors.peso}
            required
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="Volume Estimado"
            type="number"
            value={formData.volume}
            onChange={(value) => handleChange('volume', value)}
            error={errors.volume}
            required
            disabled={readOnly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Produto *
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="tipoProduto"
                value="SECOS"
                checked={formData.tipoProduto === 'SECOS'}
                onChange={(e) => handleChange('tipoProduto', e.target.value)}
                className="mr-2"
                disabled={readOnly}
              />
              SECOS
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="tipoProduto"
                value="CONGELADOS"
                checked={formData.tipoProduto === 'CONGELADOS'}
                onChange={(e) => handleChange('tipoProduto', e.target.value)}
                className="mr-2"
                disabled={readOnly}
              />
              CONGELADOS
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="tipoProduto"
                value="REFRIGERADOS"
                checked={formData.tipoProduto === 'REFRIGERADOS'}
                onChange={(e) => handleChange('tipoProduto', e.target.value)}
                className="mr-2"
                disabled={readOnly}
              />
              REFRIGERADOS
            </label>
          </div>
          {errors.tipoProduto && <p className="text-red-500 text-sm mt-1">{errors.tipoProduto}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Carga *</label>
          <select
            value={formData.tipoCarga}
            onChange={(e) => handleChange('tipoCarga', e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            disabled={readOnly}
          >
            <option value="" disabled>Selecione o tipo de carga</option>
            <option value="PALETIZADO">Paletizado</option>
            <option value="BATIDO">Batido</option>
            <option value="MISTO">Misto</option>
          </select>
          {errors.tipoCarga && <p className="text-red-500 text-sm mt-1">{errors.tipoCarga}</p>}
        </div>
        <div>
          <DatePicker
            label="Data Desejada"
            value={formData.dataDesejada}
            onChange={(date) => handleChange('dataDesejada', date ? date.toISOString().split('T')[0] + 'T00:00:00-03:00' : '')}
            error={errors.dataDesejada}
            required
            disabled={readOnly}
          />
        </div>
      </div>

      <div>
        <Input
          label="Conferente Sugerido"
          value={formData.conferente}
          onChange={(value) => handleChange('conferente', value)}
          placeholder="Nome do conferente"
          disabled={readOnly}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
        <textarea
          value={formData.observacoes}
          onChange={(e) => handleChange('observacoes', e.target.value)}
          rows={3}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          placeholder="Observações sobre o agendamento"
          disabled={readOnly}
        />
      </div>

      {!readOnly && (
        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit">
            Criar Solicitação
          </Button>
        </div>
      )}

      {readOnly && (
        <div className="flex justify-end pt-4">
          <Button onClick={onCancel}>
            Fechar
          </Button>
        </div>
      )}
    </form>
  );
};

export default Solicitacoes;
